import { GridSortDirection } from '@mui/x-data-grid-premium';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

/**
 * Smart numeric sort comparator for MUI DataGrid columns that handles sort direction.
 *
 * This function returns a comparator that:
 * - Always keeps empty/null/undefined values at the bottom regardless of sort direction
 * - Sorts numeric values correctly in ascending or descending order
 * - Handles non-numeric values gracefully
 *
 * @param sortDirection - The sort direction from MUI DataGrid ('asc', 'desc', null, or undefined)
 * @returns A comparator function for MUI DataGrid columns
 *
 * @example
 * // Apply to numeric ID columns
 * {
 *   field: "portfolio_id",
 *   headerName: "Portfolio ID",
 *   getSortComparator: getNumericStringSortComparator,
 * }
 */
export const getNumericStringSortComparator = (
  sortDirection: GridSortDirection
) => {
  const modifier = sortDirection === 'desc' ? -1 : 1;

  return (v1: unknown, v2: unknown): number => {
    // Handle empty/null/undefined values - ALWAYS sort to bottom (not affected by modifier)
    // Optimized: avoid unnecessary string coercion for non-string values
    const isEmpty1 = v1 == null || (typeof v1 === 'string' && v1.trim() === '');
    const isEmpty2 = v2 == null || (typeof v2 === 'string' && v2.trim() === '');

    if (isEmpty1 && isEmpty2) {
      return 0; // Both empty, treat as equal
    }

    if (isEmpty1) {
      return 1; // v1 is empty, always goes to bottom
    }

    if (isEmpty2) {
      return -1; // v2 is empty, always goes to bottom
    }

    // Parse as numbers for non-empty values
    const num1 = parseFloat(v1 as string);
    const num2 = parseFloat(v2 as string);

    // If both values are valid numbers, sort numerically with direction modifier
    if (!isNaN(num1) && !isNaN(num2)) {
      return modifier * (num1 - num2);
    }

    // Handle cases where one or both values are NaN
    if (isNaN(num1) && isNaN(num2)) {
      // Fall back to locale-aware string comparison for non-numeric values (Hebrew, etc.)
      // sensitivity: 'base' makes it case-insensitive
      return modifier * String(v1).localeCompare(String(v2), undefined, { numeric: true, sensitivity: 'base' });
    }

    if (isNaN(num1)) {
      return 1; // v1 is NaN, comes after v2
    }

    if (isNaN(num2)) {
      return -1; // v2 is NaN, comes after v1
    }

    return 0;
  };
};

export const isDateField = (fieldName: string): boolean =>
  /(?:^|_)DATE(?:_|$)/.test(fieldName.toUpperCase());

/**
 * Detects date format by scanning values. If any value has first component > 12,
 * format is day-first. If second component > 12, format is month-first.
 * If ambiguous, defaults to day-first. Detects separator (/ or -) from data.
 */
// separator reflects the last-seen separator; used only when no unambiguous format found early
export const detectDateFormat = (values: unknown[]): string | null => {
  let separator = '/';
  let foundDateLikeValue = false;

  for (const val of values) {
    if (val == null || typeof val !== 'string' || val.trim() === '') continue;
    const str = val.trim();

    // Detect separator
    if (str.includes('/')) separator = '/';
    else if (str.includes('-')) separator = '-';

    const parts = str.split(/[\/\-]/);
    if (parts.length !== 3) continue;

    // If first part is 4 digits, it's YYYY-MM-DD format
    if (parts[0].length === 4) return `YYYY${separator}MM${separator}DD`;

    const first = parseInt(parts[0], 10);
    const second = parseInt(parts[1], 10);
    if (isNaN(first) || isNaN(second)) continue;

    foundDateLikeValue = true;

    // If first number > 12, it must be the day (day-first format)
    if (first > 12) return `D${separator}M${separator}YYYY`;
    // If second number > 12, it must be the day (month-first format)
    if (second > 12) return `M${separator}D${separator}YYYY`;
  }

  // Values look like dates but order is ambiguous - default to day-first
  if (foundDateLikeValue) return `D${separator}M${separator}YYYY`;

  // No date-like values found at all
  return null;
};

export const createDateSortComparator = (format: string) => (
  sortDirection: GridSortDirection
) => {
  const modifier = sortDirection === 'desc' ? -1 : 1;

  return (v1: unknown, v2: unknown): number => {
    const isEmpty1 = v1 == null || (typeof v1 === 'string' && v1.trim() === '');
    const isEmpty2 = v2 == null || (typeof v2 === 'string' && v2.trim() === '');

    if (isEmpty1 && isEmpty2) {
      return 0;
    }

    if (isEmpty1) {
      return 1;
    }

    if (isEmpty2) {
      return -1;
    }

    const date1 = dayjs(String(v1), format);
    const date2 = dayjs(String(v2), format);
    const valid1 = date1.isValid();
    const valid2 = date2.isValid();

    if (valid1 && valid2) {
      return modifier * (date1.valueOf() - date2.valueOf());
    }

    if (!valid1 && !valid2) {
      return modifier * String(v1).localeCompare(String(v2), undefined, { numeric: true, sensitivity: 'base' });
    }

    if (!valid1) {
      return 1;
    }

    if (!valid2) {
      return -1;
    }

    return 0;
  };
};
